import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import VMasker from 'vanilla-masker';
import { toast } from 'react-toastify';

import { Paper, Grid, Typography, TextField, Chip, FormControlLabel, Switch, Radio, IconButton, InputAdornment } from '@mui/material';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useFormik } from 'formik';
import Loader from 'components/Loader';
import Modal from 'components/Modal';
import Input from 'components/Input';
import NumericInput from 'components/NumericInput';
import Select from 'components/Select';
import Button from 'components/Button';
import ButtonConfirmation from 'components/ButtonConfirmation';
import SchedulingEvent from 'components/SchedulingEvent';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

import { APIProvider, Map } from '@vis.gl/react-google-maps';
import ReactGoogleAutoComplete from 'react-google-autocomplete';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinusCircle, faPlusCircle, faXmarkCircle, faPen, faSearch, faClock } from '@fortawesome/free-solid-svg-icons';

import { Close as CloseIcon, KeyboardArrowLeft, KeyboardArrowRight, EventNote } from '@material-ui/icons';

import { setToken } from 'services';
import { setUserId } from 'pages/Profile/store/actions';
import { setEstablishmentId } from 'pages/BusinessInfo/store/actions';
import { loadFilters } from 'components/Filter/store/thunk';
import { loadServicesCategories } from 'pages/Services/store/thunk';
import { loadAllClients, loadClientInfo } from 'pages/ClientList/store/thunk';
import { setAllClients } from 'pages/ClientList/store/actions';
import { upsertSchedulingClient } from 'pages/ClientRegistration/components/steps/BasicInfo/store/thunk';
import { setCalendarOption } from 'pages/Scheduling/store/actions';
import { loadCalendarServices } from 'pages/Scheduling/store/thunk';
import { loadServicesScheduling, upsertServicesScheduling, loadServicesExecutionTime } from '../store/thunk';
import { clearSchedulingServices } from '../store/actions';

import { formatCurrency } from 'helpers/converters';
import { formatPhone, formatServicesCategories, formatAddress, capitalizeString } from 'helpers/formatters';

import useDidMount from 'helpers/hooks/useDidMount';
import useWindowDimensions from 'helpers/hooks/useWindowDimensions';
import useDebounce from 'helpers/hooks/useDebounce';
import useSchedulingAvailability from 'helpers/hooks/useSchedulingAvailability';

import { validations } from './validations';

import { useStyles } from './styles';

import { theme } from 'theme/globalStyles';

const DrawerIcon = require('../../../assets/img/icon_drawer.svg');

function renderEventContent (eventInfo) {
  if(eventInfo.event.display === 'background' || eventInfo.event.extendedProps?.isUnavailableSlot) {
    return true;
  }

  const { title } = eventInfo.event._def;
  const { quantity, time, services, startDateTime, endDateTime, type, isDelivery, situationId, plate, client } = eventInfo.event._def.extendedProps;

  return(
    <SchedulingEvent
      title={title}
      quantity={quantity || services?.length}
      time={time}
      startDateTime={startDateTime}
      endDateTime={endDateTime}
      type={type}
      isDelivery={isDelivery}
      situationId={situationId}
      plate={plate}
      street={client?.street}
      name={client?.name}
      phone={client?.phones}
      email={client?.email}
    />
  );
}

export default function SchedulingServicesRegistration() {
  const dispatch = useDispatch();

  const classes = useStyles();

  const { width } = useWindowDimensions();
  const isSm = width < 960;

  const history = useHistory();
  const isEdit = history.location.pathname == '/editar-agendamento-servico';
  const id = history?.location?.state?.data?.id || 'new-event';
  const newSchedulingAddress = history.location.state?.data?.newSchedulingAddress;
  const appointmentSelectionDate = history.location.state?.data?.appointmentSelectionDate;

  const didComponentMount = useDidMount();

  const calendarRef = useRef(null);
  const { unavailableEvents, handleDatesSet, dayCellClassNames, isDateUnavailable } = useSchedulingAvailability();

  const { token } = useSelector(state => state.login)
  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { selects } = useSelector(state => state.filters)
  const { servicesCategories } = useSelector(state => state.services);
  const { selectedCalendarOption, calendarOptions } = useSelector(state => state.scheduling);
  const { isLoading, submit } = useSelector(state => state.schedulingServices);
  const { calendarList } = useSelector(state => state.scheduling);
  const { allClients } = useSelector(state => state.clientList);

  const isMonthView = selectedCalendarOption == 'dayGridMonth';
  const isWeekView = selectedCalendarOption == 'timeGridWeek';
  const isDayView = selectedCalendarOption == 'timeGridDay';

  const formattedClients = allClients?.map(client => {
    let parsedPhones = [];

    try {
      parsedPhones = JSON.parse(client?.phones || []);
    } catch (error) {
      parsedPhones = [];
    }

    return {
      value: client?.clientId,
      label: parsedPhones?.length > 0 ? `${client?.name} - ${formatPhone(client?.phones)}` : client?.name,
      name: client?.name
    }
  });

  const frequencies = selects
    ?.find(filter => filter?.id == 'frequency')
    ?.options;

  const services = selects
    ?.find(filter => filter?.id == 'services')
    ?.options
    ?.map(service => {
      return {
        value: service?.serviceId,
        label: service?.description,
        categories: service?.categories?.filter(category => !!category),
        amount: service?.amount,
        quantity: 0
      }
    })
    ?.filter(service => !!service?.value);

  const formattedSubmitServices = services?.map(service => {
    const submitService = submit?.services?.find(item => item?.serviceId == service?.value);

    return {
      ...service,
      id: submitService?.id,
      amount: parseFloat(submitService?.amount ?? service?.amount ?? 0).toFixed(2),
      quantity: +submitService?.quantity || 0
    }
  });

  const formattedServices = history.location.pathname == '/novo-agendamento-servico' ? services : formattedSubmitServices;
  const [isDiscount, setIsDiscount] = useState(false);
  const [isDelivery, setIsDelivery] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isSubmitButtonClicked, setIsSubmitButtonClicked] = useState(false);
  const [serviceSearch, setServiceSearch] = useState('');
  const [servicesExecutionTime, setServicesExecutionTime] = useState(0);
  const [newEvents, setNewEvents] = useState([]);
  const [actionEventType, setActionEventType] = useState('all');

  const [selectedCalendarEventId, setSelectedCalendarEventId] = useState('');
  const [selectedCalendarEventTypeId, setSelectedCalendarEventTypeId] = useState('');

  const [isServiceEditModalOpen, setIsServiceEditModalOpen] = useState(false);
  const [serviceEdit, setServiceEdit] = useState({});
  const [previousService, setPreviousService] = useState({ amount: 0, quantity: 0 });

  const [vehicleOptions, setVehicleOptions] = useState([]);

  const [dateCycle, setDateCycle] = useState(moment(moment.now()));

  const year = moment(dateCycle, 'YYYY-MM-DD').year();
  const month = moment().month(moment(dateCycle, 'YYYY-MM-DD').month()).format('MM');
  const maxDay = moment(dateCycle, 'YYYY-MM-DD').daysInMonth();

  const initialDate = `${year}-${month}-01`;
  const finalDate = `${year}-${month}-${maxDay + 1}`;

  const [formattedClientsInput, setFormattedClientsInput] = useState('');
  const debouncedFormattedClientsInput = useDebounce(formattedClientsInput, 1000);

  useEffect(() => {
    if(debouncedFormattedClientsInput?.length > 1) {
      dispatch(loadAllClients(userId, establishmentId, debouncedFormattedClientsInput));
    }
  }, [debouncedFormattedClientsInput]);

  useEffect(() => {
    if(!token) {
      return history.push('/login');
    }

    if(token) {
      setToken(token);
    }
  }, [token]);

  useEffect(() => {
    dispatch(loadFilters(userId, establishmentId));
    dispatch(loadServicesCategories(userId));
  }, []);

  useEffect(() => {
    const setDateTimeout = setTimeout(() => {
      if(!!appointmentSelectionDate) {
        formik.setFieldValue('dateStart', moment(appointmentSelectionDate, 'YYYY-MM-DD').format('YYYY-MM-DD'));
        formik.setFieldValue('dateEnd', moment(appointmentSelectionDate, 'YYYY-MM-DD').format('YYYY-MM-DD'));

        if(!isMonthView) {
          formik.setFieldValue('timeStart', moment(appointmentSelectionDate, 'YYYY-MM-DD HH:mm').format('HH:mm'));
          formik.setFieldValue('timeEnd', moment(appointmentSelectionDate, 'YYYY-MM-DD HH:mm').add(1, 'hour').format('HH:mm'));
        }

        setDateCycle(moment(appointmentSelectionDate, 'YYYY-MM-DD'));

        const calendarApi = calendarRef.current.getApi();
        calendarApi.gotoDate(moment(appointmentSelectionDate, 'YYYY-MM-DD HH:mm:ss').toDate());
      }
    }, 1000);

    return () => clearTimeout(setDateTimeout);
  }, [appointmentSelectionDate]);

  useEffect(() => {
    if(+submit?.discount?.amount > 0) {
      return setIsDiscount(true);
    }

    setIsDiscount(false);
  }, [submit?.discount?.amount]);

  useEffect(() => {
    if(+submit?.recurrence?.frequencyId > 0) {
      return setIsRecurring(true);
    }

    setIsRecurring(false);
  }, [submit?.recurrence?.frequencyId]);

  useEffect(() => {
    if(+submit?.situationId == 2) {
      return setIsConfirmed(true);
    }

    setIsConfirmed(false);
  }, [submit?.situationId]);

  useEffect(() => {
    if(!!submit?.delivery?.address || !!newSchedulingAddress) {
      return setIsDelivery(true);
    }

    setIsDelivery(false);
  }, [submit?.delivery, newSchedulingAddress]);

  const getAddressDetails = address => {
    if(!address) {
      return {}
    }

    const street = address?.find(place => place?.types?.includes('route'))?.long_name || '';
    const streetNumber = address?.find(place => place?.types?.includes('street_number'))?.short_name || '';
    const district = address?.find(place => place?.types?.includes('sublocality'))?.long_name || '';
    const city = address?.find(place => place?.types?.includes('administrative_area_level_2'))?.long_name || '';
    const state = address?.find(place => place?.types?.includes('administrative_area_level_1'))?.short_name || '';
    const postalCode = address?.find(place => place?.types?.includes('postal_code'))?.long_name || '';

    return {
      street,
      streetNumber,
      district,
      city,
      state,
      postalCode
    }
  }

  const getClientAddressLatLng = async address => {
    if(!address) {
      return [];
    }

    let results = [];
    let status = 'ZERO_RESULTS';

    try {
      await window.google.maps.importLibrary('geocoding');

      const { geocode } = new window.google.maps.Geocoder();

      await geocode({ address })
        .then(data => {
          results = data?.results;
          status = 'OK';
        })
        .catch(() => {
          results = [];
          status = 'ZERO_RESULTS';
        });
    } catch (error) {
      return [];
    }

    if(status == 'ZERO_RESULTS') {
      return [];
    }

    if(status == 'OK') {
      return [
        results[0].geometry.location.lat(),
        results[0].geometry.location.lng(),
        results[0]?.address_components
      ];
    }

    return [];
  }

  useEffect(() => {
    if(!!submit?.client?.name) {
      dispatch(loadAllClients(userId, establishmentId, submit?.client?.name));
      setFormattedClientsInput(submit?.client?.name);
    }

    if(!submit?.client?.name) {
      dispatch(setAllClients([]));
      setFormattedClientsInput('');
    }
  }, [submit?.client?.name]);

  useEffect(() => {
    if(isMonthView || isDayView) {
      return;
    }

    const { start, end } = isMonthView ? getCalendarValidMonthRange() : getCalendarValidRange();

    const extraProps = {
      initialDate: start,
      finalDate: isMonthView ? `${year}-${month}-${maxDay}` : end
    }

    dispatch(loadCalendarServices(userId, establishmentId, extraProps, ''));
  }, [initialDate, finalDate, dateCycle]);

  useEffect(() => {
    if(isMonthView && didComponentMount) {
      const { start, end } = isMonthView ? getCalendarValidMonthRange() : getCalendarValidRange();

      let extraProps = {
        initialDate: start,
        finalDate: isMonthView ? `${year}-${month}-${maxDay}` : end
      }

      if(!!appointmentSelectionDate) {
        const year = moment(appointmentSelectionDate, 'YYYY-MM-DD').year();
        const month = moment().month(moment(appointmentSelectionDate, 'YYYY-MM-DD').month()).format('MM');
        const maxDay = moment(appointmentSelectionDate, 'YYYY-MM-DD').daysInMonth();

        extraProps = {
          initialDate: `${year}-${month}-01`,
          finalDate: `${year}-${month}-${maxDay}`
        }
      }

      dispatch(loadCalendarServices(userId, establishmentId, extraProps, ''));
    }
  }, []);

  useEffect(() => {
    if(isDayView && didComponentMount) {
      const { start, end } = getCalendarValidRange();

      let extraProps = {
        initialDate: start,
        finalDate: end
      }

      if(!!appointmentSelectionDate) {
        const year = moment(appointmentSelectionDate, 'YYYY-MM-DD HH:mm').year();
        const month = moment().month(moment(appointmentSelectionDate, 'YYYY-MM-DD HH:mm').month()).format('MM');
        const day = moment(appointmentSelectionDate, 'YYYY-MM-DD HH:mm').format('D');

        extraProps = {
          initialDate: `${year}-${month}-${day}`,
          finalDate: `${year}-${month}-${day}`
        }
      }

      dispatch(loadCalendarServices(userId, establishmentId, extraProps, ''));
    }
  }, []);

  useEffect(() => {
    if(id && id != 'new-event') {
      dispatch(loadServicesScheduling(userId, establishmentId, id));
    }

    if(history.location.pathname == '/novo-agendamento-servico') {
      dispatch(clearSchedulingServices());
    }
  }, [id]);

  useEffect(() => {
    if(calendarList?.length == 0) {
      const title = formik.values.availableServices?.filter(service => +service?.quantity > 0)[0]?.label || '';

      setNewEvents([
        {
          id: 'new-event',
          title,
          dateStart: moment(moment.now()).format('YYYY-MM-DD'),
          dateEnd: moment(moment.now()).format('YYYY-MM-DD'),
          timeStart: '',
          timeEnd: '',
          startDateTime: `${formik.values.dateStart} ${formik.values.timeStart}:00`,
          endDateTime: `${formik.values.dateEnd} ${formik.values.timeEnd}:00`,
          start: `${formik.values.dateStart} ${formik.values.timeStart}`,
          end: `${formik.values.dateEnd} ${formik.values.timeEnd}`,
          time: `${formik.values.timeStart} - ${formik.values.timeEnd}`
        }
      ]);
    }

    if(calendarList?.length > 0) {
      const formattedCalendarList = calendarList?.map(event => {
        return {
          ...event,
          title: event.title,
          start: moment(event.startDateTime, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD HH:mm:ss'),
          end: moment(event.endDateTime, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD HH:mm:ss'),
          time: `${moment(event.startDateTime, 'YYYY-MM-DD HH:mm:ss').format('HH:mm')} - ${moment(event.endDateTime, 'YYYY-MM-DD HH:mm:ss').format('HH:mm')}`
        }
      });

      let events = formattedCalendarList;

      if(id == 'new-event') {
        const title = formik.values.availableServices?.filter(service => +service?.quantity > 0)[0]?.label || '';

        events = [
          ...events,
          {
            id: 'new-event',
            title,
            dateStart: moment(moment.now()).format('YYYY-MM-DD'),
            dateEnd: moment(moment.now()).format('YYYY-MM-DD'),
            timeStart: '',
            timeEnd: '',
            startDateTime: `${formik.values.dateStart} ${formik.values.timeStart}:00`,
            endDateTime: `${formik.values.dateEnd} ${formik.values.timeEnd}:00`,
            start: `${formik.values.dateStart} ${formik.values.timeStart}`,
            end: `${formik.values.dateEnd} ${formik.values.timeEnd}`,
            time: `${formik.values.timeStart} - ${formik.values.timeEnd}`
          }
        ]
      }

      setNewEvents(events);
    }
  }, [calendarList]);

  const handleUpdateSingleServiceAmountAndQuantity = (service, amount, quantity) => {
    const { value } = service;

    const updatedServices = formik.values.availableServices?.map(service => {
      if(+service?.value == +value) {
        return {
          ...service,
          amount,
          quantity
        }
      }

      return service;
    });

    return formik.setFieldValue('availableServices', updatedServices);
  }

  const handleUpdateServicesQuantity = (quantity, operation) => {
    if(operation == 'add') {
      return quantity + 1;
    }

    if(operation == 'sub') {
      if(quantity == 0) {
        return 0;
      }

      return quantity - 1;
    }

    return quantity;
  }

  const handleServicesUpdate = (service, operation) => {
    const { value } = service;

    const updatedServices = formik.values.availableServices?.map(service => {
      if(+service?.value == +value) {
        return {
          ...service,
          quantity: handleUpdateServicesQuantity(service?.quantity, operation)
        }
      }

      return service;
    });

    return formik.setFieldValue('availableServices', updatedServices);
  }

  const handleRemoveServices = service => {
    const { value } = service;

    const updatedServices = formik.values.availableServices?.map(service => {
      if(service?.value == value) {
        return {
          ...service,
          quantity: 0
        }
      }

      return service;
    });

    return formik.setFieldValue('availableServices', updatedServices);
  }

  const handleSelectServices = () => {
    const services = formik.values.availableServices?.filter(service => +service?.quantity > 0);

    if(services?.length > 0) {
      const updatedEvents = newEvents.map(event => {
        if(event?.id == id) {
          return {
            ...event,
            title: services[0]?.label,
            quantity: services?.length
          }
        }

        return event;
      });

      setNewEvents(updatedEvents);
    }

    setServiceSearch('');
  }

  const getCalendarValidRange = () => {
    if(isDayView) {
      return {
        start: dateCycle.format('YYYY-MM-DD'),
        end: dateCycle.format('YYYY-MM-DD')
      }
    }

    if(isWeekView) {
      return {
        start: dateCycle.startOf('week').format('YYYY-MM-DD'),
        end: dateCycle.clone().endOf('week').add(1, 'days').format('YYYY-MM-DD')
      }
    }
  }

  const getCalendarValidMonthRange = () => {
    const end = moment(dateCycle, 'YYYY-MM-DD').endOf('month').add(1, 'days').format('YYYY-MM-DD');

    return {
      start: initialDate,
      end
    }
  }

  const handleDateCycleChange = date => {
    if(isDayView) {
      if(date == 'prev') {
        const newDateCycle = moment(dateCycle.subtract(1, 'days'));
        setDateCycle(newDateCycle);

        const extraProps = {
          initialDate: newDateCycle.format('YYYY-MM-DD'),
          finalDate: newDateCycle.format('YYYY-MM-DD')
        }

        dispatch(loadCalendarServices(userId, establishmentId, extraProps, '')).then(() => {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.prev();
        });
      }

      if(date == 'next') {
        const newDateCycle = moment(dateCycle.add(1, 'days'));
        setDateCycle(newDateCycle);

        const extraProps = {
          initialDate: newDateCycle.format('YYYY-MM-DD'),
          finalDate: newDateCycle.format('YYYY-MM-DD')
        }

        dispatch(loadCalendarServices(userId, establishmentId, extraProps, '')).then(() => {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.next();
        });
      }
    }

    if(isWeekView) {
      if(date == 'prev') {
        return setDateCycle(moment(dateCycle.subtract(1, 'week')));
      }
  
      if(date == 'next') {
        return setDateCycle(moment(dateCycle.add(1, 'week')));
      }
    }

    if(isMonthView) {
      if(date == 'prev') {
        const newDateCycle = moment(dateCycle.subtract(1, 'month'));
        setDateCycle(newDateCycle);

        const extraProps = {
          initialDate: newDateCycle.startOf('month').format('YYYY-MM-DD'),
          finalDate: newDateCycle.endOf('month').format('YYYY-MM-DD')
        }

        dispatch(loadCalendarServices(userId, establishmentId, extraProps, '')).then(() => {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.prev();
        });
      }

      if(date == 'next') {
        const newDateCycle = moment(dateCycle.add(1, 'month'));
        setDateCycle(newDateCycle);

        const extraProps = {
          initialDate: newDateCycle.startOf('month').format('YYYY-MM-DD'),
          finalDate: newDateCycle.endOf('month').format('YYYY-MM-DD')
        }

        dispatch(loadCalendarServices(userId, establishmentId, extraProps, '')).then(() => {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.next();
        });
      }
    }
  }

  const getServicesTotal = (discount, services) => {
    const { amount, type } = discount || {};

    const servicesAmount = services?.reduce((total, service) => total + (+service.amount * +service.quantity), 0);

    if(+type == 1) {
      return formatCurrency(servicesAmount - amount);
    }

    if(+type == 2) {
      return formatCurrency(servicesAmount - (servicesAmount * amount / 100));
    }

    return formatCurrency(servicesAmount);
  }

  const getVehicleLabel = vehicle => {
    const { plate, vehicleModel, vehicleColor } = vehicle || {};

    if(!vehicleModel && !vehicleColor) {
      return plate;
    }

    if(!!vehicleModel && !vehicleColor) {
      return `${plate} - ${vehicleModel}`;
    }

    if(!vehicleModel && !!vehicleColor) {
      return `${plate} (${vehicleColor})`;
    }

    return `${plate} - ${vehicleModel} (${vehicleColor})`;
  }

  const handleSchedulingSubmit = async values => {
    const { availableServices, recurrence, discount, clientId, client, vehicle, delivery } = values;

    if(availableServices?.filter(service => +service?.quantity > 0)?.length == 0) {
      return;
    }

    const [latitude, longitude, address] = await getClientAddressLatLng(delivery?.address);
    const { street, streetNumber, district, city, state, postalCode } = getAddressDetails(address) || {};

    let clientParams = {
      name: formattedClientsInput,
      ...(!!client?.phone) && {
        phones: [client?.phone]
      },
      ...(!!client?.email) && {
        email: client?.email
      },
      ...(delivery?.addressType == 'address') && {
        ...(!!postalCode && { postalCode }),
        ...(!!street && { street }),
        ...(!!streetNumber && { streetNumber }),
        ...(!!district && { district }),
        ...(!!city && { city }),
        ...(!!state && { state })
      }
    }

    let params = {
      startDateTime: `${values?.dateStart} ${values?.timeStart}:00`,
      endDateTime: `${values?.dateEnd} ${values?.timeEnd}:00`,
      situationId: isConfirmed ? 2 : 1,
      typeId: 1,
      clientId
    }

    if(!!clientParams?.name || !!clientParams?.phones || !!clientParams?.email) {
      await dispatch(upsertSchedulingClient(userId, establishmentId, clientId, clientParams)).then(({ clientId }) => {
        params = {
          ...params,
          clientId
        }

        formik.setFieldValue('clientId', clientId);
        dispatch(loadAllClients(userId, establishmentId, formattedClientsInput));
      });
    }

    const formattedServices = availableServices
      ?.filter(service => +service?.quantity > 0)
      ?.map(service => {
        return {
          id: service?.id,
          serviceId: service?.value,
          serviceName: service?.label,
          quantity: service?.quantity,
          amount: service?.amount,
          executionTime: 0
        }
      });

    params = {
      ...params,
      services: formattedServices
    }

    if(!!latitude && !!longitude) {
      params = {
        ...params,
        latitude,
        longitude
      }
    }

    if(+recurrence?.frequencyId > 0) {
      params = {
        ...params,
        recurrence: {
          ...recurrence,
          ...!!submit?.recurrenceId && {
            applyToAll: actionEventType == 'all' ? 1 : 0
          },
          frequencyId: !isRecurring ? 0 : recurrence?.frequencyId
        }
      }
    }

    if(!!delivery?.address) {
      params = {
        ...params,
        completeAddress: delivery?.address
      }
    }

    if(!!discount?.amount) {
      params = {
        ...params,
        discount
      }
    }

    if(!!vehicle?.plate) {
      params = {
        ...params,
        vehicle
      }
    }

    dispatch(upsertServicesScheduling(userId, establishmentId, values?.id, params))
      .then(() => {
        if(isSm) {
          return history.push('/agendamento');
        }

        const { start, end } = isMonthView ? getCalendarValidMonthRange() : getCalendarValidRange();

        const extraProps = {
          initialDate: start,
          finalDate: isMonthView ? `${year}-${month}-${maxDay}` : end
        }

        dispatch(loadCalendarServices(userId, establishmentId, extraProps, ''));

        if(!values?.id) {
          setServicesExecutionTime(0);
          setIsSubmitButtonClicked(false);
          setIsDiscount(false);
          setIsDelivery(false);
          setIsRecurring(false);
          setIsConfirmed(false);
          setFormattedClientsInput('');
          dispatch(setAllClients([]));
          formik.resetForm();
        }
      });
  }

  const getInitialValues = () => {
    if(newSchedulingAddress) {
      return {
        ...submit,
        availableServices: formattedServices,
        delivery: {
          ...submit.delivery,
          addressType: 'others',
          address: newSchedulingAddress
        }
      }
    }

    return {
      ...submit,
      availableServices: formattedServices
    }
  }

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validationSchema: validations,
    initialValues: getInitialValues(),
    onSubmit: values => handleSchedulingSubmit(values)
  });

  useEffect(() => {
    if(servicesExecutionTime > 0) {
      const endDateTime = moment(`${formik.values.dateStart} ${formik.values.timeStart}:00`, 'YYYY-MM-DD HH:mm:ss').add(servicesExecutionTime, 'minutes').format('YYYY-MM-DD HH:mm:ss');
      const [endDate, endTime] = endDateTime?.split(' ');

      if(!formik.values.timeStart) {
        formik.setFieldValue('timeStart', '00:00');
      }

      formik.setFieldValue('dateEnd', moment(endDate, 'YYYY-MM-DD').format('YYYY-MM-DD'));
      formik.setFieldValue('timeEnd', endTime?.slice(0, 5));
    }
  }, [servicesExecutionTime, formik.values.dateStart, formik.values.timeStart]);

  useEffect(() => {
    const servicesIds = formik.values.availableServices
      ?.filter(service => +service?.quantity > 0)
      ?.map(service => service?.value);

    if(servicesIds?.length > 0) {
      dispatch(loadServicesExecutionTime(userId, establishmentId, { services: servicesIds }))
        .then(data => setServicesExecutionTime(data));
    }
  }, [formik.values.availableServices]);

  useEffect(() => {
    if(formik.values.dateStart && formik.values.dateEnd && formik.values.timeStart && formik.values.timeEnd) {
      const updatedEvents = newEvents.map(event => {
        if(event?.id == id) {
          const title = formik.values.availableServices?.filter(service => +service?.quantity > 0)[0]?.label || '';

          return {
            ...event,
            title,
            startDateTime: `${formik.values.dateStart} ${formik.values.timeStart}:00`,
            endDateTime: `${formik.values.dateEnd} ${formik.values.timeEnd}:00`,
            start: `${formik.values.dateStart} ${formik.values.timeStart}`,
            end: `${formik.values.dateEnd} ${formik.values.timeEnd}`,
            time: `${formik.values.timeStart} - ${formik.values.timeEnd}`
          }
        }

        return event;
      });

      setNewEvents(updatedEvents);
    }
  }, [formik.values, dateCycle]);

  useEffect(() => {
    if(!didComponentMount) {
      if(isDayView) {
        const start = moment(calendarRef.current?.getApi()?.getDate()).format('YYYY-MM-DD');
        const end = moment(calendarRef.current?.getApi()?.getDate()).format('YYYY-MM-DD');

        const extraProps = {
          initialDate: start,
          finalDate: end
        }

        dispatch(loadCalendarServices(userId, establishmentId, extraProps, ''));
      }

      if(isWeekView) {
        const { start, end } = getCalendarValidRange() || {};

        const extraProps = {
          initialDate: start,
          finalDate: end
        }

        dispatch(loadCalendarServices(userId, establishmentId, extraProps, ''));
      }

      if(isMonthView) {
        const { start, end } = getCalendarValidMonthRange() || {};

        const extraProps = {
          initialDate: start,
          finalDate: end
        }

        dispatch(loadCalendarServices(userId, establishmentId, extraProps, ''));
      }
    }
  }, [isDayView, isWeekView, isMonthView]);

  return(
    <Paper style={{ height: '100%' }}>
      {!!selectedCalendarEventId && (
        <Modal
          id='scheduling-calendar-event-edit'
          title='Edição de evento'
          maxWidth='sm'
          open={!!selectedCalendarEventId}
          onClose={() => {
            setSelectedCalendarEventId('');
            setSelectedCalendarEventTypeId('');
          }}
        >
          <Typography color={theme.palette.primary.main}>
            Deseja editar este evento?
          </Typography>
          <Grid container xs={12} justifyContent='flex-end' alignItems='center' gap='10px'>
            <Button
              type='button'
              color={theme.palette.success.main}
              onClick={() => {
                dispatch(setUserId(userId));
                dispatch(setEstablishmentId({ establishmentId }));

                if(selectedCalendarEventTypeId == 1) {
                  history.push({
                    pathname: '/editar-agendamento-servico',
                    state: {
                      data: {
                        id: selectedCalendarEventId
                      }
                    }
                  });

                  setSelectedCalendarEventId('');
                  setSelectedCalendarEventTypeId('');
                }

                if(selectedCalendarEventTypeId == 2) {
                  return history.push({
                    pathname: '/editar-agendamento',
                    state: {
                      data: {
                        id: selectedCalendarEventId
                      }
                    }
                  });
                }
              }}
            >
              Sim
            </Button>
            <Button
              type='button'
              color={theme.palette.error.main}
              onClick={() => {
                setSelectedCalendarEventId('');
                setSelectedCalendarEventTypeId('');
              }}
            >
              Não
            </Button>
          </Grid>
        </Modal>
      )}
      {isServiceEditModalOpen && (
        <Modal
          id='scheduling-service-edit'
          title='Editar serviço'
          maxWidth='sm'
          open={isServiceEditModalOpen}
          onClose={() => {
            handleUpdateSingleServiceAmountAndQuantity(serviceEdit, previousService.amount, previousService.quantity);
            setServiceEdit({});
            setIsServiceEditModalOpen(false);
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'stretch', marginTop: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <Typography color={theme.palette.primary.main}>{serviceEdit?.label}</Typography>
                <Typography color='textSecondary'>Categoria(s): {formatServicesCategories(serviceEdit?.categories, servicesCategories)}</Typography>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10 }}>
                  <IconButton
                    style={{ padding: 0 }}
                    onClick={() => handleServicesUpdate(serviceEdit, 'sub')}
                  >
                    <FontAwesomeIcon
                      icon={faMinusCircle}
                      color={theme.palette.primary.main}
                      size="xs"
                    />
                  </IconButton>
                  <Typography color={theme.palette.primary.main}>
                    {formik.values.availableServices?.find(service => +service?.value == +serviceEdit?.value)?.quantity}
                  </Typography>
                  <IconButton
                    style={{ padding: 0 }}
                    onClick={() => handleServicesUpdate(serviceEdit, 'add')}
                  >
                    <FontAwesomeIcon
                      icon={faPlusCircle}
                      color={theme.palette.primary.main}
                      size="xs"
                    />
                  </IconButton>
                </div>
              </div>
            </div>
            <div style={{ marginTop: 20, marginBottom: 20 }}>
              <NumericInput
                name='serviceAmount'
                label='Valor unitário'
                value={formik.values.availableServices?.find(service => +service?.value == +serviceEdit?.value)?.amount}
                onChange={value => handleUpdateSingleServiceAmountAndQuantity(serviceEdit, value, formik.values.availableServices?.find(service => +service?.value == +serviceEdit?.value)?.quantity)}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
              <Typography color='textSecondary'>
                Total: {formatCurrency(formik.values.availableServices?.find(service => +service?.value == +serviceEdit?.value)?.amount * formik.values.availableServices?.find(service => +service?.value == +serviceEdit?.value)?.quantity)}
              </Typography>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 10 }}>
              <div style={{ width: '50%' }}>
                <Button
                  fullWidth
                  type='button'
                  color='tertiary'
                  variant='outlined'
                  onClick={() => {
                    handleUpdateSingleServiceAmountAndQuantity(serviceEdit, previousService.amount, previousService.quantity);
                    setServiceEdit({});
                    setIsServiceEditModalOpen(false);
                  }}
                >
                  Cancelar
                </Button>
              </div>
              <div style={{ width: '50%' }}>
                <Button
                  fullWidth
                  type='button'
                  color='success'
                  onClick={() => {
                    setServiceEdit({});
                    setIsServiceEditModalOpen(false);
                  }}
                >
                  Editar
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
      <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_KEY}>
        <Map
          style={{ height: 0, width: 0 }}
          mapId='scheduling-map'
          disableDefaultUI
          defaultZoom={0}
        />
      </APIProvider>
      <Grid container style={{ height: '100%' }}>
        <Grid item xs={isSm ? 12 : 5} md={isSm ? 12 : 3} style={{ backgroundColor: '#FCFCFC', height: '100vh', padding: 10 }}>
          <form
            onSubmit={formik.handleSubmit}
            style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}
          >
            <div style={{ height: '100%', overflowY: 'scroll' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 5 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <img src={DrawerIcon} />
                  <Typography color='textSecondary' style={{ marginLeft: 5 }}>
                    {isEdit ? 'Editar agendamento' : 'Novo agendamento'}
                  </Typography>
                </div>
                <div>
                  <IconButton onClick={() => history.push('/agendamento')}>
                    <CloseIcon />
                  </IconButton>
                </div>
              </div>
              <Loader isLoading={isLoading} style={{ height: '100%' }}>
                <Grid container>
                  <Grid item xs={12}>
                    <Input
                      type='text'
                      name='servicesSearch'
                      label='Pesquisar serviço'
                      value={serviceSearch}
                      error={isSubmitButtonClicked && formik.values.availableServices?.filter(service => +service?.quantity > 0)?.length == 0}
                      helperText={(isSubmitButtonClicked && formik.values.availableServices?.filter(service => +service?.quantity > 0)?.length == 0) && 'Selecione pelo menos um serviço'}
                      onChange={event => setServiceSearch(event.target.value)}
                      endAdornment={
                        <InputAdornment position='end'>
                          <FontAwesomeIcon
                            color='#022A5C'
                            size='sx'
                            icon={faSearch}
                          />
                        </InputAdornment>
                      }
                    />
                  </Grid>
                  {(!serviceSearch && formik.values.availableServices?.filter(service => +service?.quantity > 0)?.length > 0) && (
                    <Grid item xs={12} style={{ backgroundColor: '#F3FBFF', border: '1px solid #E4E7EA', borderRadius: 5, padding: 5 }}>
                      {formik.values.availableServices?.filter(service => +service?.quantity > 0)?.map(service => {
                        return(
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                              <Typography color={theme.palette.primary.main}>
                                {service?.quantity}x {service?.label}
                              </Typography>
                              <Typography color='textSecondary'>
                                Categoria(s): {formatServicesCategories(service?.categories, servicesCategories)}
                              </Typography>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <Typography color={theme.palette.primary.main}>
                                {formatCurrency(service?.amount * service?.quantity)}
                              </Typography>
                              <IconButton
                                style={{ padding: 0 }}
                                onClick={() => handleRemoveServices(service)}
                              >
                                <FontAwesomeIcon
                                  icon={faXmarkCircle}
                                  color="#F66C6B"
                                  size="xs"
                                />
                              </IconButton>
                              <IconButton
                                style={{ padding: 0 }}
                                onClick={() => {
                                  setIsServiceEditModalOpen(true);
                                  setServiceEdit(service);
                                  setPreviousService({ amount: service?.amount, quantity: service?.quantity })
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faPen}
                                  color="#022A5C"
                                  size="xs"
                                />
                              </IconButton>
                            </div>
                          </div>
                        );
                      })}
                      <div style={{ border: '1px solid #E4E7EA', marginTop: 10, marginBottom: 5 }} />
                      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: 5 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography color={theme.palette.primary.main}>
                            <b>Subtotal</b>
                          </Typography>
                          <Typography color={theme.palette.primary.main}>
                            <b>
                              {formatCurrency(formik.values.availableServices?.filter(service => +service?.quantity > 0)?.reduce((total, service) => total + (+service.amount * +service.quantity), 0))}
                            </b>
                          </Typography>
                        </div>
                        {(+formik.values.discount?.amount > 0) && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography color={theme.palette.primary.main}>
                              <b>Desconto</b>
                            </Typography>
                            <Typography color={theme.palette.primary.main}>
                              <b>
                                {(+formik.values.discount.type == 2) ? `${formik.values.discount?.amount || 0}%` : formatCurrency(formik.values.discount?.amount)}
                              </b>
                            </Typography>
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography color={theme.palette.primary.main}>
                            <b>TOTAL</b>
                          </Typography>
                          <Typography color={theme.palette.primary.main}>
                            <b>
                              {getServicesTotal(formik.values.discount, formik.values.availableServices)}
                            </b>
                          </Typography>
                        </div>
                        {(servicesExecutionTime > 0) && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography color={theme.palette.primary.main}>
                              <b>Tempo de execução</b>
                            </Typography>
                            <Typography color={theme.palette.primary.main}>
                              <b>{moment.duration(servicesExecutionTime, 'minutes').format('D[d] HH[h] mm[min]')}</b>
                            </Typography>
                          </div>
                        )}
                      </div>
                    </Grid>
                  )}
                  {serviceSearch?.length > 0 && (
                    <Grid item xs={12} style={{ backgroundColor: '#FFFFFF', border: '1px solid #D9D9D9', borderRadius: 5, padding: 5 }}>
                      <Button
                        fullWidth
                        type='button'
                        color='success'
                        onClick={handleSelectServices}
                        style={{ margin: '10px 0 10px 0' }}
                      >
                        Adicionar Serviços
                      </Button>
                      {formik.values.availableServices?.filter(service => service?.label?.toLowerCase()?.includes(serviceSearch?.toLowerCase()))?.map(service => {
                        return(
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', marginTop: 10 }}>
                            <div>
                              <Typography color={theme.palette.primary.main}>{service?.label}</Typography>
                              <Typography color='textSecondary'>Categoria(s): {formatServicesCategories(service?.categories, servicesCategories)}</Typography>
                            </div>
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10 }}>
                                <Typography color={theme.palette.primary.main}>
                                  {formatCurrency(service?.amount)}
                                </Typography>
                                <IconButton
                                  style={{ padding: 0 }}
                                  onClick={() => handleServicesUpdate(service, 'sub')}
                                >
                                  <FontAwesomeIcon
                                    icon={faMinusCircle}
                                    color={theme.palette.primary.main}
                                    size="xs"
                                  />
                                </IconButton>
                                <Typography color={theme.palette.primary.main}>
                                  {service?.quantity}
                                </Typography>
                                <IconButton
                                  style={{ padding: 0 }}
                                  onClick={() => handleServicesUpdate(service, 'add')}
                                >
                                  <FontAwesomeIcon
                                    icon={faPlusCircle}
                                    color={theme.palette.primary.main}
                                    size="xs"
                                  />
                                </IconButton>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          name="discount"
                          color="primary"
                          value={isDiscount}
                          checked={isDiscount}
                          onChange={event => setIsDiscount(event.target.checked)}
                          sx={{
                            '&.MuiSwitch-root .Mui-checked': {
                              color: theme.palette.primary.main
                            }
                          }}
                        />
                      }
                      label={
                        <Typography color={theme.palette.primary.main}>Desconto</Typography>
                      }
                    />
                  </Grid>
                  {isDiscount && (
                    <Grid item xs={12}>
                      <Grid container>
                        <Grid item xs={6} style={{ padding: 10 }}>
                          <FormControlLabel
                            name='discount.type'
                            label={
                              <Typography color='textSecondary'>
                                Percentual (%)
                              </Typography>
                            }
                            control={
                              <Radio
                                style={{ padding: 2.5 }}
                                checked={+formik.values?.discount?.type == 2}
                                onClick={event => {
                                  formik.setFieldValue(event.target.name, '2');
                                  formik.setFieldValue('discount.amount', '');
                                }}
                                sx={{
                                  color: theme.palette.primary.main,
                                  '&, &.Mui-checked': {
                                    color: theme.palette.primary.main
                                  }
                                }}
                              />
                            }
                          />
                        </Grid>
                        <Grid item xs={6} style={{ padding: 10 }}>
                          <FormControlLabel
                            name='discount.type'
                            label={
                              <Typography color='textSecondary'>
                                Valor inteiro
                              </Typography>
                            }
                            control={
                              <Radio
                                style={{ padding: 2.5 }}
                                checked={+formik.values?.discount?.type == 1}
                                onClick={event => {
                                  formik.setFieldValue(event.target.name, '1');
                                  formik.setFieldValue('discount.amount', '');
                                }}
                                sx={{
                                  color: theme.palette.primary.main,
                                  '&, &.Mui-checked': {
                                    color: theme.palette.primary.main
                                  }
                                }}
                              />
                            }
                          />
                        </Grid>
                        {(+formik.values?.discount?.type == 2) && (
                          <Grid item xs={12}>
                            <Input
                              type='number'
                              name='discount.amount'
                              label='Desconto'
                              value={formik.values?.discount?.amount}
                              onChange={event => {
                                if(event.target.value > 100) {
                                  return;
                                }

                                formik.setFieldValue('discount.amount', event.target.value);
                              }}
                            />
                          </Grid>
                        )}
                        {(+formik.values?.discount?.type == 1) && (
                          <Grid item xs={12}>
                            <NumericInput
                              name='discount.amount'
                              label='Desconto'
                              value={formik.values?.discount?.amount}
                              onChange={value => formik.setFieldValue('discount.amount', value)}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  )}
                  <Grid xs={12}>
                    <Typography color={theme.palette.primary.main} style={{ borderBottom: '1px solid #E4E7EA', marginTop: 10, marginBottom: 10 }}>
                      <b>Período do agendamento</b>
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          <DatePicker
                            inputFormat='DD/MM/YYYY'
                            value={formik.values.dateStart}
                            onChange={date => {
                              formik.setFieldValue('dateStart', moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD'));
                              formik.setFieldValue('dateEnd', moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD'));
                            }}
                            components={{
                              OpenPickerIcon: () => (
                                <EventNote style={{ color: '#1E5168' }} />
                              )
                            }}
                            InputProps={{
                              endAdornment: isSm && (
                                <EventNote style={{ color: '#1E5168' }} />
                              )
                            }}
                            renderInput={params => (
                              <TextField
                                {...params}
                                fullWidth
                                name='dateStart'
                                variant='outlined'
                                error={formik.errors.dateStart}
                                helperText={formik.errors.dateStart}
                                inputProps={{
                                  ...params.inputProps,
                                  style: {
                                    height: 20,
                                    color: theme.palette.primary.main
                                  }
                                }}
                                SelectProps={{
                                  MenuProps: {
                                    disableEnforceFocus: true
                                  }
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused fieldset': {
                                      borderColor: theme.palette.primary.main
                                    }
                                  }
                                }}
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={6}>
                        <Input
                          type='timeRangePadStart'
                          name='timeStart'
                          label='Hora início'
                          value={formik.values.timeStart}
                          error={formik.errors.timeStart}
                          helperText={formik.errors.timeStart}
                          onChange={event => {
                            if(event.target.value?.length > 5) {
                              return;
                            }

                            formik.setFieldValue('timeStart', event.target.value);
                          }}
                          endAdornment={
                            <InputAdornment position='end'>
                              <FontAwesomeIcon
                                color='#022A5C'
                                size='sx'
                                icon={faClock}
                              />
                            </InputAdornment>
                          }
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          <DatePicker
                            inputFormat='DD/MM/YYYY'
                            value={formik.values.dateEnd}
                            onChange={date => {
                              if(moment(date, 'DD/MM/YYYY').isBefore(moment(formik.values.dateStart, 'YYYY-MM-DD'))) {
                                return toast.error('A data final precisa ser maior que a data inicial.');
                              }

                              formik.setFieldValue('dateEnd', moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD'));
                            }}
                            components={{
                              OpenPickerIcon: () => (
                                <EventNote style={{ color: '#1E5168' }} />
                              )
                            }}
                            InputProps={{
                              endAdornment: isSm && (
                                <EventNote style={{ color: '#1E5168' }} />
                              )
                            }}
                            renderInput={params => (
                              <TextField
                                {...params}
                                fullWidth
                                name='dateEnd'
                                variant='outlined'
                                error={formik.errors.dateEnd}
                                helperText={formik.errors.dateEnd}
                                inputProps={{
                                  ...params.inputProps,
                                  style: {
                                    height: 20,
                                    color: theme.palette.primary.main
                                  }
                                }}
                                SelectProps={{
                                  MenuProps: {
                                    disableEnforceFocus: true
                                  }
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused fieldset': {
                                      borderColor: theme.palette.primary.main
                                    }
                                  }
                                }}
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={6}>
                        <Input
                          type='timeRangePadStart'
                          name='timeEnd'
                          label='Hora fim'
                          value={formik.values.timeEnd}
                          error={formik.errors.timeEnd}
                          helperText={formik.errors.timeEnd}
                          onChange={event => {
                            if(event.target.value?.length > 5) {
                              return;
                            }

                            formik.setFieldValue('timeEnd', event.target.value);
                          }}
                          endAdornment={
                            <InputAdornment position='end'>
                              <FontAwesomeIcon
                                color='#022A5C'
                                size='sx'
                                icon={faClock}
                              />
                            </InputAdornment>
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          name="recurring"
                          color="primary"
                          value={isRecurring}
                          checked={isRecurring}
                          onChange={event => setIsRecurring(event.target.checked)}
                          sx={{
                            '&.MuiSwitch-root .Mui-checked': {
                              color: theme.palette.primary.main
                            }
                          }}
                        />
                      }
                      label={
                        <Typography color={theme.palette.primary.main}>Agendamento recorrente</Typography>
                      }
                    />
                  </Grid>
                  {isRecurring && (
                    <>
                      <Grid item xs={12}>
                        <Select
                          name='recurrence.frequencyId'
                          label='Selecione a recorrência'
                          options={frequencies}
                          value={formik.values?.recurrence?.frequencyId}
                          onChange={formik.handleChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          <DatePicker
                            inputFormat='DD/MM/YYYY'
                            value={formik.values?.recurrence?.endDate}
                            label='Data fim da recorrência'
                            onChange={date => formik.setFieldValue('recurrence.endDate', moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD'))}
                            renderInput={params => (
                              <TextField
                                {...params}
                                fullWidth
                                name='recurrence.endDate'
                                variant='outlined'
                                inputProps={{
                                  ...params.inputProps,
                                  style: {
                                    height: 20,
                                    color: theme.palette.primary.main
                                  }
                                }}
                                SelectProps={{
                                  MenuProps: {
                                    disableEnforceFocus: true
                                  }
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused fieldset': {
                                      borderColor: theme.palette.primary.main
                                    }
                                  }
                                }}
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </>
                  )}
                  <Grid xs={12}>
                    <Typography color={theme.palette.primary.main} style={{ borderBottom: '1px solid #E4E7EA', marginTop: 10, marginBottom: 10 }}>
                      <b>Informações do cliente</b>
                    </Typography>
                    <Autocomplete
                      id='clientId'
                      classes={classes}
                      options={formattedClients || []}
                      value={formattedClients?.find(client => client.value == formik.values?.clientId) || {}}
                      inputValue={formattedClientsInput || ''}
                      onInputChange={(_, value, reason) => {
                        if(reason == 'clear') {
                          setFormattedClientsInput('');
                          formik.setFieldValue('delivery.addressType', 'others');
                          formik.setFieldValue('delivery.address', '');
                        }

                        if(reason != 'reset') {
                          setFormattedClientsInput(value);
                        }
                      }}
                      onChange={(_, params) => {
                        const { value, name } = params || {};

                        if(!value) {
                          setFormattedClientsInput('');
                          setVehicleOptions([]);

                          formik.setFieldValue('clientId', '');
                          formik.setFieldValue('client.name', '');
                          formik.setFieldValue('client.email', '');
                          formik.setFieldValue('client.phone', '');
                          formik.setFieldValue('client.address', '');
                          formik.setFieldValue('delivery.addressType', 'others');

                          return;
                        }

                        setFormattedClientsInput(name);
                        formik.setFieldValue('clientId', value);

                        dispatch(loadClientInfo(userId, establishmentId, value)).then(client => {
                          const hasPhone = !!client?.phones && client?.phones?.filter(phone => !!phone)?.length > 0;
                          const phone = hasPhone ? client?.phones[0] : '';
                          const address = formatAddress(client);

                          formik.setFieldValue('client.name', client?.name || '');
                          formik.setFieldValue('client.email', client?.email || '');
                          formik.setFieldValue('client.phone', phone || '');
                          formik.setFieldValue('client.address', address || '');

                          if(!!address) {
                            if(!newSchedulingAddress) {
                              formik.setFieldValue('delivery.addressType', 'address');
                              formik.setFieldValue('delivery.address', address || '');
                            }
                          }

                          if(!address) {
                            formik.setFieldValue('delivery.addressType', 'address');
                            formik.setFieldValue('delivery.address', '');
                          }

                          if(client?.vehicles?.length == 1) {
                            formik.setFieldValue('vehicle.plate', client?.vehicles[0]?.plate);
                            formik.setFieldValue('vehicle.vehicleModel', client?.vehicles[0]?.vehicleModel);
                            formik.setFieldValue('vehicle.vehicleColor', client?.vehicles[0]?.vehicleColor);
                          }

                          if(client?.vehicles?.length > 1) {
                            const vehicles = client?.vehicles?.map(vehicle => {
                              return {
                                value: vehicle?.plate,
                                label: getVehicleLabel(vehicle),
                                model: vehicle?.vehicleModel,
                                color: vehicle?.vehicleColor
                              }
                            });
  
                            setVehicleOptions([
                              { value: '', label: '--' },
                              ...vehicles
                            ]);
                          }
                        });
                      }}
                      getOptionLabel={option => option.label}
                      renderInput={params => (
                        <TextField
                          {...params}
                          variant="outlined"
                          color="primary"
                          label="Nome do cliente"
                          inputProps={{
                            ...params.inputProps,
                            style: {
                              height: 20,
                              color: theme.palette.primary.main
                            }
                          }}
                          InputLabelProps={{
                            sx: {
                              '&.Mui-focused': {
                                color: theme.palette.primary.main
                              }
                            }
                          }}
                          SelectProps={{
                            MenuProps: {
                              disableEnforceFocus: true
                            }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main
                              }
                            }
                          }}
                        />
                      )}
                    />
                    <Input
                      type='phone'
                      name='client.phone'
                      label='Telefone'
                      value={formatPhone(formik.values?.client?.phone) || ''}
                      onChange={formik.handleChange}
                    />
                    <Input
                      type='email'
                      name='client.email'
                      label='E-mail'
                      value={formik.values?.client?.email || ''}
                      error={formik.errors.client?.email}
                      helperText={formik.errors.client?.email}
                      onChange={formik.handleChange}
                    />
                  </Grid>
                  <Grid xs={12}>
                    <Typography color={theme.palette.primary.main} style={{ borderBottom: '1px solid #E4E7EA', marginTop: 10, marginBottom: 10 }}>
                      <b>Informações do veículo (opcional)</b>
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Autocomplete
                          id='vehicle.plate'
                          classes={classes}
                          options={vehicleOptions}
                          value={vehicleOptions?.find(vehicle => vehicle.value == formik.values?.vehicle?.plate) || {}}
                          inputValue={formik.values?.vehicle?.plate || ''}
                          onInputChange={(_, value, reason) => {
                            if(reason == 'clear') {
                              formik.setFieldValue('vehicle.plate', '');
                            }

                            if(reason != 'reset') {
                              formik.setFieldValue('vehicle.plate', VMasker.toAlphaNumeric(value).toUpperCase());
                            }
                          }}
                          onChange={(_, params) => {
                            const { value, model, color } = params || {};

                            formik.setFieldValue('vehicle.plate', value);
                            formik.setFieldValue('vehicle.vehicleModel', model);
                            formik.setFieldValue('vehicle.vehicleColor', color);
                          }}
                          getOptionLabel={option => option.label}
                          renderInput={params => (
                            <TextField
                              {...params}
                              variant="outlined"
                              color="primary"
                              label="Placa"
                              inputProps={{
                                ...params.inputProps,
                                style: {
                                  height: 20,
                                  color: theme.palette.primary.main
                                }
                              }}
                              InputLabelProps={{
                                sx: {
                                  '&.Mui-focused': {
                                    color: theme.palette.primary.main
                                  }
                                }
                              }}
                              SelectProps={{
                                MenuProps: {
                                  disableEnforceFocus: true
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '&.Mui-focused fieldset': {
                                    borderColor: theme.palette.primary.main
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Input
                          type='text'
                          name='vehicle.vehicleModel'
                          label='Modelo'
                          value={formik.values?.vehicle?.vehicleModel || ''}
                          error={formik.errors?.vehicle?.vehicleModel}
                          helperText={formik.errors?.vehicle?.vehicleModel}
                          onChange={formik.handleChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Input
                          type='text'
                          name='vehicle.vehicleColor'
                          label='Cor'
                          value={formik.values?.vehicle?.vehicleColor || ''}
                          error={formik.errors?.vehicle?.vehicleColor}
                          helperText={formik.errors?.vehicle?.vehicleColor}
                          onChange={formik.handleChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              name="delivery"
                              color="primary"
                              value={isDelivery}
                              checked={isDelivery}
                              onChange={event => setIsDelivery(event.target.checked)}
                              sx={{
                                '&.MuiSwitch-root .Mui-checked': {
                                  color: theme.palette.primary.main
                                }
                              }}
                            />
                          }
                          label={
                            <Typography color={theme.palette.primary.main}>Delivery</Typography>
                          }
                        />
                      </Grid>
                      {isDelivery && (
                        <>
                          <Grid item xs={12}>
                            <Select
                              name='delivery.addressType'
                              options={[
                                { value: 'others', label: 'Outros' },
                                { value: 'address', label: 'Endereço do cliente' }
                              ]}
                              value={formik.values?.delivery?.addressType}
                              onChange={event => {
                                formik.setFieldValue(event.target.name, event.target.value);

                                if(event.target.value == 'address') {
                                  formik.setFieldValue('delivery.address', formik?.values?.client?.address || '');
                                }

                                if(event.target.value == 'others') {
                                  formik.setFieldValue('delivery.address', '');
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            {!formik.values?.delivery?.address && (
                              <ReactGoogleAutoComplete
                                id='delivery.address'
                                name='delivery.address'
                                placeholder='Endereço'
                                defaultValue=''
                                apiKey={process.env.REACT_APP_GOOGLE_MAPS_KEY}
                                libraries={['places']}
                                language='pt-BR'
                                options={{
                                  types: [],
                                  componentRestrictions: {
                                    country: 'br'
                                  }
                                }}
                                onPlaceSelected={place => formik.setFieldValue('delivery.address', place?.formatted_address)}
                                onFocus={event => {
                                  event.target.autocomplete = 'off';
                                }}
                                onKeyDown={event => {
                                  if(+event.keyCode == 13) {
                                    return event.preventDefault();
                                  }
                                }}
                              />
                            )}
                            {!!formik.values?.delivery?.address && (
                              <ReactGoogleAutoComplete
                                id='delivery.address'
                                name='delivery.address'
                                placeholder='Endereço'
                                value={formik.values?.delivery?.address}
                                apiKey={process.env.REACT_APP_GOOGLE_MAPS_KEY}
                                libraries={['places']}
                                language='pt-BR'
                                options={{
                                  types: [],
                                  componentRestrictions: {
                                    country: 'br'
                                  }
                                }}
                                onPlaceSelected={place => formik.setFieldValue('delivery.address', place?.formatted_address)}
                                onFocus={event => {
                                  event.target.autocomplete = 'off';
                                }}
                                onKeyDown={event => {
                                  if(+event.keyCode == 13) {
                                    return event.preventDefault();
                                  }
                                }}
                              />
                            )}
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Loader>
            </div>
            {!isLoading && (
              <div>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="delivery"
                        color="primary"
                        value={isConfirmed}
                        checked={isConfirmed}
                        onChange={event => setIsConfirmed(event.target.checked)}
                        sx={{
                          '&.MuiSwitch-root .Mui-checked': {
                            color: theme.palette.primary.main
                          }
                        }}
                      />
                    }
                    label={
                      <Typography color={theme.palette.primary.main}>Agendamento confirmado</Typography>
                    }
                  />
                </Grid>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 10 }}>
                  <div style={{ width: isSm ? '50%' : 'fit-content' }}>
                    <Button
                      fullWidth={isSm}
                      type='button'
                      color='tertiary'
                      variant='outlined'
                      onClick={() => history.push('/agendamento')}
                    >
                      Cancelar
                    </Button>
                  </div>
                  <div style={{ width: isSm ? '50%' : 'fit-content' }}>
                    {!!submit?.recurrenceId && (
                      <ButtonConfirmation
                        fullWidth={isSm}
                        title='Editar'
                        color='primary'
                        isCancelButton={false}
                        modalTitle='Editar agendamento'
                        modalText={
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <FormControlLabel
                              style={{ backgroundColor: (actionEventType == 'single') ? '#F0F2F6' : '#FCFCFC', border: '1px solid #D7D7D7', borderRadius: 5, margin: 0 }}
                              control={
                                <Radio
                                  style={{ color: '#1E5168' }}
                                  checked={actionEventType == 'single'}
                                  onChange={() => setActionEventType('single')}
                                />
                              }
                              label={
                                <Typography color='textSecondary' variant='body2'>
                                  Este evento
                                </Typography>
                              }
                            />
                            <FormControlLabel
                              style={{ backgroundColor: (actionEventType == 'all') ? '#F0F2F6' : '#FCFCFC', border: '1px solid #D7D7D7', borderRadius: 5, margin: 0 }}
                              control={
                                <Radio
                                  style={{ color: '#1E5168' }}
                                  checked={actionEventType == 'all'}
                                  onChange={() => setActionEventType('all')}
                                />
                              }
                              label={
                                <Typography color='textSecondary' variant='body2'>
                                  Este e os seguintes eventos
                                </Typography>
                              }
                            />
                          </div>
                        }
                        modalButtons={[
                          {
                            fullWidth: true,
                            color: 'success',
                            title: 'Editar',
                            onClick: () => formik.handleSubmit(formik.values)
                          }
                        ]}
                      />
                    )}
                    {!submit?.recurrenceId && (
                      <Button
                        fullWidth={isSm}
                        type='submit'
                        color='primary'
                        onClick={() => setIsSubmitButtonClicked(true)}
                      >
                        {isEdit ? 'Editar' : 'Salvar'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </form>
        </Grid>
        {!isSm && (
          <Grid item xs={7} md={9} style={{ height: '100%' }}>
            <Grid container>
              <Grid item xs={12} style={{ height: '5vh' }}>
                <Grid container>
                  <Grid item xs={10} style={{ display: 'flex', alignItems: 'center', paddingLeft: 10 }}>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <IconButton
                        style={{ backgroundColor: 'rgba(21, 56, 72, 0.2)', color: theme.palette.primary.main }}
                        onClick={() => handleDateCycleChange('prev')}
                      >
                        <KeyboardArrowLeft />
                      </IconButton>
                      <IconButton
                        style={{ backgroundColor: 'rgba(21, 56, 72, 0.2)', color: theme.palette.primary.main }}
                        onClick={() => handleDateCycleChange('next')}
                      >
                        <KeyboardArrowRight />
                      </IconButton>
                    </div>
                    <Typography
                      style={{ marginLeft: 10 }}
                      color={theme.palette.primary.main}
                      variant='h6'
                    >
                      {isDayView && (
                        <>
                          {dateCycle.format('DD/MMM')}
                        </>
                      )}
                      {isWeekView && (
                        <>
                          {dateCycle.startOf('week').format('DD/MMM')} - {dateCycle.endOf('week').format('DD/MMM')}
                        </>
                      )}
                      {isMonthView && (
                        <>
                          {capitalizeString(dateCycle.format('MMMM'))} {dateCycle.format('YYYY')}
                        </>
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} style={{ paddingRight: 5 }}>
                    <Select
                      options={calendarOptions}
                      value={selectedCalendarOption}
                      onChange={event => dispatch(setCalendarOption(event.target.value))}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ height: '95vh', padding: '0 5px 5px 5px' }}>
                {(selectedCalendarOption == 'dayGridMonth') && (
                  <FullCalendar
                    ref={calendarRef}
                    locale='pt-br'
                    plugins={[ interactionPlugin, dayGridPlugin, timeGridPlugin ]}
                    headerToolbar={{
                      start: '',
                      center: '',
                      end: ''
                    }}
                    dayHeaderContent={option => {
                      if(isMonthView) {
                        return (
                          <div>
                            <Typography color='textSecondary' variant='body2'>
                              {capitalizeString(moment(option.date).add(1, 'day').format('ddd'))}
                            </Typography>
                          </div>
                        );
                      }

                      if(!isMonthView) {
                        return (
                          <div>
                            {option.isToday ? (
                              <Chip
                                style={{ height: 20, backgroundColor: '#13BC4B' }}
                                label={
                                  <Typography style={{ color: '#FFFFFF' }}>
                                    {moment(option.date).format('DD/MMM')}
                                  </Typography>
                                }
                              />
                            ) : (
                              <Typography color={theme.palette.primary.main}>
                                {moment(option.date).format('DD/MMM')}
                              </Typography>
                            )}
                            <Typography color='textSecondary' variant='body2'>
                              {moment(option.date).format('ddd').toUpperCase()}
                            </Typography>
                          </div>
                        );
                      }
                    }}
                    dayCellContent={option => {
                      if(isMonthView) {
                        return (
                          <div>
                            {option.isToday ? (
                              <div style={{ textAlign: 'end' }}>
                                <Chip
                                  style={{ height: 20, backgroundColor: '#13BC4B' }}
                                  label={
                                    <Typography style={{ color: '#FFFFFF' }}>
                                      {moment(option.date).format('DD')}
                                    </Typography>
                                  }
                                />
                              </div>
                            ) : (
                              <div style={{ textAlign: 'end' }}>
                                <Typography color={theme.palette.primary.main}>
                                  {moment(option.date).format('DD')}
                                </Typography>
                              </div>
                            )}
                          </div>
                        );
                      }
                    }}
                    datesSet={handleDatesSet}
                    dayCellClassNames={dayCellClassNames}
                    events={newEvents?.map(list => {
                      return {
                        ...list,
                        start: moment(list?.startDateTime, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD'),
                        end: moment(list?.endDateTime, 'YYYY-MM-DD HH:mm:ss').add(1, 'days').format('YYYY-MM-DD')
                      }
                    })}
                    eventContent={renderEventContent}
                    eventOrder={function (a, b) {
                      return moment(a.startDateTime, 'YYYY-MM-DD HH:mm:ss') - moment(b.startDateTime, 'YYYY-MM-DD HH:mm:ss')
                    }}
                    eventClick={({ event }) => {
                      if(event.extendedProps?.isUnavailableSlot) {
                        return;
                      }

                      const { type } = event._def.extendedProps;
                      const { publicId } = event._def;

                      setSelectedCalendarEventTypeId(type);
                      setSelectedCalendarEventId(publicId);
                    }}
                    dateClick={({ date, allDay }) => {
                      if(isDateUnavailable(date, allDay)) {
                        return;
                      }

                      const calendarApi = calendarRef.current.getApi();
                      calendarApi.gotoDate(date);

                      setDateCycle(moment(date));

                      formik.setFieldValue('dateStart', moment(date).format('YYYY-MM-DD'));
                      formik.setFieldValue('dateEnd', moment(date).format('YYYY-MM-DD'));
                    }}
                    dayMaxEvents={6}
                    moreLinkContent='Mais eventos'
                    initialView='dayGridMonth'
                    validRange={{}}
                    firstDay={1}
                    allDaySlot={false}
                    expandRows={true}
                    fixedWeekCount={false}
                    height='100%'
                  />
                )}
                {(selectedCalendarOption == 'timeGridWeek') && (
                  <FullCalendar
                    ref={calendarRef}
                    locale='pt-br'
                    plugins={[ interactionPlugin, dayGridPlugin, timeGridPlugin ]}
                    headerToolbar={{
                      start: '',
                      center: '',
                      end: ''
                    }}
                    dayHeaderContent={option => {
                      if(isMonthView) {
                        return (
                          <div>
                            <Typography color='textSecondary' variant='body2'>
                              {capitalizeString(moment(option.date).add(1, 'day').format('ddd'))}
                            </Typography>
                          </div>
                        );
                      }

                      if(!isMonthView) {
                        return (
                          <div>
                            {option.isToday ? (
                              <Chip
                                style={{ height: 20, backgroundColor: '#13BC4B' }}
                                label={
                                  <Typography style={{ color: '#FFFFFF' }}>
                                    {moment(option.date).format('DD/MMM')}
                                  </Typography>
                                }
                              />
                            ) : (
                              <Typography color={theme.palette.primary.main}>
                                {moment(option.date).format('DD/MMM')}
                              </Typography>
                            )}
                            <Typography color='textSecondary' variant='body2'>
                              {moment(option.date).format('ddd').toUpperCase()}
                            </Typography>
                          </div>
                        );
                      }
                    }}
                    dayCellContent={option => {
                      if(isMonthView) {
                        return (
                          <div>
                            {option.isToday ? (
                              <div style={{ textAlign: 'end' }}>
                                <Chip
                                  style={{ height: 20, backgroundColor: '#13BC4B' }}
                                  label={
                                    <Typography style={{ color: '#FFFFFF' }}>
                                      {moment(option.date).format('DD')}
                                    </Typography>
                                  }
                                />
                              </div>
                            ) : (
                              <div style={{ textAlign: 'end' }}>
                                <Typography color={theme.palette.primary.main}>
                                  {moment(option.date).format('DD')}
                                </Typography>
                              </div>
                            )}
                          </div>
                        );
                      }
                    }}
                    datesSet={handleDatesSet}
                    dayCellClassNames={dayCellClassNames}
                    events={[
                      ...unavailableEvents,
                      ...(newEvents?.map(list => {
                        delete list['allDay'];
                        return list;
                      }) || [])
                    ]}
                    eventContent={renderEventContent}
                    eventClick={({ event }) => {
                      if(event.extendedProps?.isUnavailableSlot) {
                        return;
                      }

                      const { type } = event._def.extendedProps;
                      const { publicId } = event._def;

                      setSelectedCalendarEventTypeId(type);
                      setSelectedCalendarEventId(publicId);
                    }}
                    dateClick={({ date, allDay }) => {
                      if(isDateUnavailable(date, allDay)) {
                        return;
                      }

                      const calendarApi = calendarRef.current.getApi();
                      calendarApi.gotoDate(date);

                      setDateCycle(moment(date));

                      formik.setFieldValue('dateStart', moment(date).format('YYYY-MM-DD'));
                      formik.setFieldValue('dateEnd', moment(date).format('YYYY-MM-DD'));
                      formik.setFieldValue('timeStart', moment(date).format('HH:mm'));
                      formik.setFieldValue('timeEnd', moment(date).add(1, 'hour').format('HH:mm'));
                    }}
                    initialView='timeGridWeek'
                    validRange={getCalendarValidRange}
                    firstDay={null}
                    allDaySlot={false}
                    slotMinTime='04:00:00'
                    slotMaxTime='23:59:59'
                    expandRows={true}
                    fixedWeekCount={false}
                    height='100%'
                  />
                )}
                {(selectedCalendarOption == 'timeGridDay') && (
                  <FullCalendar
                    ref={calendarRef}
                    locale='pt-br'
                    plugins={[ interactionPlugin, dayGridPlugin, timeGridPlugin ]}
                    headerToolbar={{
                      start: '',
                      center: '',
                      end: ''
                    }}
                    dayHeaderContent={option => {
                      if(isMonthView) {
                        return (
                          <div>
                            <Typography color='textSecondary' variant='body2'>
                              {capitalizeString(moment(option.date).add(1, 'day').format('ddd'))}
                            </Typography>
                          </div>
                        );
                      }

                      if(!isMonthView) {
                        return (
                          <div>
                            {option.isToday ? (
                              <Chip
                                style={{ height: 20, backgroundColor: '#13BC4B' }}
                                label={
                                  <Typography style={{ color: '#FFFFFF' }}>
                                    {moment(option.date).format('DD/MMM')}
                                  </Typography>
                                }
                              />
                            ) : (
                              <Typography color={theme.palette.primary.main}>
                                {moment(option.date).format('DD/MMM')}
                              </Typography>
                            )}
                            <Typography color='textSecondary' variant='body2'>
                              {moment(option.date).format('ddd').toUpperCase()}
                            </Typography>
                          </div>
                        );
                      }
                    }}
                    dayCellContent={option => {
                      if(isMonthView) {
                        return (
                          <div>
                            {option.isToday ? (
                              <div style={{ textAlign: 'end' }}>
                                <Chip
                                  style={{ height: 20, backgroundColor: '#13BC4B' }}
                                  label={
                                    <Typography style={{ color: '#FFFFFF' }}>
                                      {moment(option.date).format('DD')}
                                    </Typography>
                                  }
                                />
                              </div>
                            ) : (
                              <div style={{ textAlign: 'end' }}>
                                <Typography color={theme.palette.primary.main}>
                                  {moment(option.date).format('DD')}
                                </Typography>
                              </div>
                            )}
                          </div>
                        );
                      }
                    }}
                    datesSet={handleDatesSet}
                    dayCellClassNames={dayCellClassNames}
                    events={[
                      ...unavailableEvents,
                      ...(newEvents?.map(list => {
                        delete list['allDay'];
                        return list;
                      }) || [])
                    ]}
                    eventContent={renderEventContent}
                    eventClick={({ event }) => {
                      if(event.extendedProps?.isUnavailableSlot) {
                        return;
                      }

                      const { type } = event._def.extendedProps;
                      const { publicId } = event._def;

                      setSelectedCalendarEventTypeId(type);
                      setSelectedCalendarEventId(publicId);
                    }}
                    dateClick={({ date, allDay }) => {
                      if(isDateUnavailable(date, allDay)) {
                        return;
                      }

                      const calendarApi = calendarRef.current.getApi();
                      calendarApi.gotoDate(date);

                      setDateCycle(moment(date));

                      formik.setFieldValue('dateStart', moment(date).format('YYYY-MM-DD'));
                      formik.setFieldValue('dateEnd', moment(date).format('YYYY-MM-DD'));
                      formik.setFieldValue('timeStart', moment(date).format('HH:mm'));
                      formik.setFieldValue('timeEnd', moment(date).add(1, 'hour').format('HH:mm'));
                    }}
                    initialView='timeGridDay'
                    validRange={{}}
                    firstDay={null}
                    allDaySlot={false}
                    slotMinTime='04:00:00'
                    slotMaxTime='23:59:59'
                    expandRows={true}
                    fixedWeekCount={false}
                    height='100%'
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
}